import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import type { UserOption } from '@/shared/hooks/useUserOptions';
import { AttachmentUploader } from './AttachmentUploader';
import { DEMO_PRIORITY_OPTIONS, PROJECT_TYPE_OPTIONS } from '../constants';
import type { AttachmentUpload, CreateDemoPayload, DemoPriority } from '../types';

interface DemoFormModalProps {
  open: boolean;
  loading?: boolean;
  canAssign?: boolean;
  users?: UserOption[];
  /** Future Lead → Demo conversion can pre-fill this; no picker UI added. */
  leadId?: string;
  onClose: () => void;
  onSubmit: (payload: CreateDemoPayload) => void;
}

interface FormState {
  title: string;
  description: string;
  priority: DemoPriority;
  project_type: string;
  expected_delivery_date: string;
  client_name: string;
  client_company: string;
  client_contact: string;
  client_mobile: string;
  client_email: string;
  requirements: string;
  expected_features: string;
  assigned_to: string;
}

const EMPTY: FormState = {
  title: '', description: '', priority: 'medium', project_type: 'website', expected_delivery_date: '',
  client_name: '', client_company: '', client_contact: '', client_mobile: '', client_email: '',
  requirements: '', expected_features: '', assigned_to: '',
};

export function DemoFormModal({ open, loading, canAssign, users = [], leadId, onClose, onSubmit }: DemoFormModalProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [attachments, setAttachments] = useState<AttachmentUpload[]>([]);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setAttachments([]);
    }
  }, [open]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const payload: CreateDemoPayload = {
      title: form.title.trim(),
      description: form.description.trim(),
      priority: form.priority,
      project_type: form.project_type,
      expected_delivery_date: form.expected_delivery_date || undefined,
      client: {
        name: form.client_name.trim(),
        company: form.client_company.trim(),
        contact_person: form.client_contact.trim(),
        mobile: form.client_mobile.trim(),
        email: form.client_email.trim(),
      },
      requirements: form.requirements.trim(),
      expected_features: form.expected_features.trim(),
      attachments,
      ...(leadId ? { lead_id: leadId } : {}),
      ...(canAssign && form.assigned_to ? { assigned_to: form.assigned_to } : {}),
    };
    onSubmit(payload);
  };

  const assigneeOptions = [{ value: '', label: '— Unassigned —' }, ...users.map((u) => ({ value: u.id, label: u.display_name }))];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Demo Request"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" form="demo-form" loading={loading}>Create Demo</Button>
        </>
      }
    >
      <form id="demo-form" onSubmit={submit} className="space-y-4">
        <Input label="Title *" name="demo-title" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Demo title…" required />

        <div className="grid grid-cols-2 gap-3">
          <Select label="Priority" name="demo-priority" value={form.priority} onChange={(e) => set('priority', e.target.value as DemoPriority)} options={DEMO_PRIORITY_OPTIONS} />
          <Select label="Project Type" name="demo-type" value={form.project_type} onChange={(e) => set('project_type', e.target.value)} options={PROJECT_TYPE_OPTIONS} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Expected Delivery" name="demo-delivery" type="date" value={form.expected_delivery_date} onChange={(e) => set('expected_delivery_date', e.target.value)} />
          {canAssign && (
            <Select label="Assign To" name="demo-assignee" value={form.assigned_to} onChange={(e) => set('assigned_to', e.target.value)} options={assigneeOptions} />
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Client Name" name="demo-client-name" value={form.client_name} onChange={(e) => set('client_name', e.target.value)} />
          <Input label="Client Company" name="demo-client-company" value={form.client_company} onChange={(e) => set('client_company', e.target.value)} />
          <Input label="Contact Person" name="demo-client-contact" value={form.client_contact} onChange={(e) => set('client_contact', e.target.value)} />
          <Input label="Mobile" name="demo-client-mobile" value={form.client_mobile} onChange={(e) => set('client_mobile', e.target.value)} />
        </div>
        <Input label="Client Email" name="demo-client-email" type="email" value={form.client_email} onChange={(e) => set('client_email', e.target.value)} />

        <div>
          <label htmlFor="demo-req" className="mb-1.5 block text-body-sm font-medium text-on-surface">Requirements</label>
          <textarea id="demo-req" rows={3} value={form.requirements} onChange={(e) => set('requirements', e.target.value)} className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40" />
        </div>

        <div>
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Attachments (optional)</label>
          <AttachmentUploader value={attachments} onChange={setAttachments} />
        </div>
      </form>
    </Modal>
  );
}
